<script setup lang="ts">
import { ref, defineProps, watch, Ref, computed } from "vue";
import { useChallengeForm } from "@/composables/tasks/challenge/useChallengeForm";
import { useRecord } from "@/composables/tasks/challenge/useChallengeRecords";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { ChallengeProgressEntry, ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types";

import ModalBox from "@/components/v2/Modal/ModalBox.vue";

// ===================================================================
// Props
// ===================================================================
interface Props {
    configuration: ChallengeConfiguration;
    show: boolean;
    onClose?: () => void;
}
const props = defineProps<Props>();

// ===================================================================
// STATE - Modal
// ===================================================================
const showModal: Ref<boolean> = ref(props.show);

// ===================================================================
// COMPOSABLES
// ===================================================================
const {
    challenged,
    weight,
    comment,
    challengeDuration,
    successMessage,

    isWeightDisabled,
    isChallengedDisabled,
    canSave,

    incrementChallenged,
    decrementChallenged,
    resetForm
} = useChallengeForm();

const { createChallengeRecord } = useRecord();
const { getEquipmentConfigurations } = useEquipment();

// ===================================================================
// Administration Equipment
// ===================================================================
// Get administration equipment configuration
const administrationEquipment = computed(() => {
    return getEquipmentConfigurations.value.find(config =>
        config.equipmentType.name.toLowerCase() === "administration equipment"
    );
});

// Get administration equipment options
const administrationEquipmentOptions = computed(() => {
    if (administrationEquipment.value?.equipmentType?.configOptions) {
        const typeOption = administrationEquipment.value.equipmentType.configOptions.find(opt => opt.name === "type");
        return typeOption?.options || [];
    }
    return [];
});

// Update the admin equipment type
const updateAdminEquipmentType = (type: string) => {
    if (administrationEquipment.value) {
        administrationEquipment.value.values.type = type;
        administrationEquipment.value.updated_at = new Date().toISOString();
    }
};

// ===================================================================
// ACTIONS - Modal
// ===================================================================
const closeModal = () => {
    if (props.onClose) {
        props.onClose();
    }
};

// ===================================================================
// ACTIONS - Save Challenge Record
// ===================================================================
const handleSaveChallengeData = (keepModalOpen = false) => {
    if (!canSave.value || !props.configuration) return;

    createChallengeRecord(props.configuration.key, {
        challenged: challenged.value,
        weight: weight.value,
        comment: comment.value,
        challengeDuration: props.configuration.challengeDuration || challengeDuration.value,
        // Include the administration equipment type
        administrationEquipment: administrationEquipment.value?.values?.type || null
    } as ChallengeProgressEntry);

    resetForm();

    if (!keepModalOpen) {
        return closeModal();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(
    () => props.show,
    (newVal) => {
        showModal.value = newVal;
    }
);
</script>

<template>
    <ModalBox v-if="showModal" @close="closeModal">
        <template #header>
            <h1 class="text-xl font-semibold">Record Challenge Administration</h1>
        </template>

        <template #main>
            <!-- Administration Equipment Selection -->
            <div v-if="administrationEquipmentOptions.length > 0 && administrationEquipment" class="mb-4">
                <label class="block text-gray-700 mb-2">Administration Equipment:</label>
                <select
                    v-model="administrationEquipment.values.type"
                    @change="updateAdminEquipmentType(administrationEquipment.values.type)"
                    class="form-select bg-white p-2.5 block w-full border border-gray-300 rounded shadow-sm"
                >
                    <option v-for="option in administrationEquipmentOptions" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <!-- Challenge input -->
            <div class="mb-4">
                <label class="block text-gray-700 mb-2">Administer Challenge:</label>
                <div class="flex items-stretch gap-2">
                    <input type="number" v-model.number="challenged" :disabled="isChallengedDisabled" min="1" pattern="\d*"
                           class="form-input p-2 block flex-1 border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed" />
                    <div class="flex flex-row gap-1">
                        <button @click="incrementChallenged" :disabled="isChallengedDisabled"
                                class="p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold">
                            +
                        </button>
                        <button @click="decrementChallenged" :disabled="isChallengedDisabled || challenged <= 1"
                                class="p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold">
                            −
                        </button>
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <!-- Weight input -->
                <label class="block text-gray-700 mb-2">Weight:</label>
                <input type="number" v-model="weight" :disabled="isWeightDisabled === true" min="0"
                       class="form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
            </div>


            <!-- Comment input -->
            <div class="mb-4">
                <label class="block text-gray-700 mb-2">Comment:</label>
                <textarea v-model="comment" placeholder="Leave a comment"
                          class="form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"></textarea>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between w-full">
                <div class="flex items-center gap-2">
                    <button
                        @click="handleSaveChallengeData(false)" type="button" :disabled="!canSave"
                        class="text-white bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Save and close
                    </button>
                    <button
                        @click="handleSaveChallengeData(true)" type="button" :disabled="!canSave || successMessage !== ''"
                        class="text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ successMessage !== "" ? "Save " + successMessage : "Save" }}
                    </button>
                </div>
                <button
                    @click="closeModal" type="button"
                    class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
                >
                    Back
                </button>
            </div>
        </template>
    </ModalBox>
</template>